import TableSearchInput from '@/components/common/TableSearchInput';
import { usePharmacistShipments } from '@/context/shipments/PharmacistShipments';
import type { ListConsignmentDto } from '@montugroup/contracts-warehousing-api';
import {
  Box,
  Checkbox,
  Chip,
  Link,
  PaginationLabelVariant,
  PaginationVariant,
  Stack,
  Table,
} from '@montugroup/design-system';
import { useDebouncedCallback } from '@montugroup/frontend-common';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import Warning from '@mui/icons-material/Warning';
import { debounce, FormControlLabel } from '@mui/material';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const MIN_TABLE_HEIGHT = 400;
function ConsignmentAcknowledgementsTable() {
  const {
    shipments,
    loading,
    handleSortingOrderChange,
    shouldResetPageIndex,
    pageSize,
    handlePaginationModelChange,
    referenceFilter,
    setReferenceFilter,
    setShouldResetPageIndex,
    setStatusFilter,
    statusFilter,
    beforeCursor,
    afterCursor,
  } = usePharmacistShipments();

  const navigate = useNavigate();
  const handleReferenceSearch = useDebouncedCallback(setReferenceFilter, 500);

  const [showReceived, setShowReceived] = useState(statusFilter.includes('RECEIVED'));

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerHeight, setContainerHeight] = useState(MIN_TABLE_HEIGHT);

  useLayoutEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const containerTop = containerRef.current.getBoundingClientRect().top;
        const viewportHeight = window.innerHeight;

        const FOOTER_HEIGHT = 155; // px
        const availableHeight = viewportHeight - containerTop - FOOTER_HEIGHT;

        setContainerHeight(Math.max(availableHeight, MIN_TABLE_HEIGHT));
      }
    };

    handleResize();
    const debounced = debounce(handleResize, 100);
    window.addEventListener('resize', debounced);
    return () => window.removeEventListener('resize', debounced);
  }, []);

  useEffect(() => {
    if (showReceived) {
      setStatusFilter(['IN_TRANSIT', 'RECEIVED']);
    } else {
      setStatusFilter(['IN_TRANSIT']);
    }
    setShouldResetPageIndex(true);
  }, [showReceived]);

  const columns: ColumnDef<ListConsignmentDto>[] = useMemo(() => {
    return [
      {
        accessorKey: 'id',
        header: 'Consignment Reference',
        enableSorting: true,
        cell: ({ row }) => <Box>{row.original.reference}</Box>,
      },
      {
        accessorKey: 'dispatchedDate',
        header: 'Dispatched Date',
        cell: ({ row }) => <Box>{new Date(row.original.dispatchedDate).toLocaleDateString()}</Box>,
        enableSorting: true,
      },
      {
        accessorKey: 'modifiedAt',
        header: 'Signed Date',
        cell: ({ row }) => (
          <Box>{row.original.status === 'RECEIVED' ? new Date(row.original.modifiedAt).toLocaleDateString() : ''}</Box>
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: true,
        meta: {
          sx: { textAlign: 'center' },
        },
        cell: ({ row }) => (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {row.original.status === 'RECEIVED' ? (
              <Chip
                size="medium"
                label="Received"
                variant="outlined"
                color="success"
                sx={{
                  backgroundColor: (theme) => theme.palette.success.main + '15',
                  color: (theme) => theme.palette.success.main,
                  fontWeight: 500,
                }}
              />
            ) : (
              <Chip
                size="medium"
                label="In Transit"
                variant="outlined"
                color="secondary"
                sx={{
                  backgroundColor: (theme) => theme.palette.secondary.main + '15',
                  color: (theme) => theme.palette.secondary.main,
                  fontWeight: 500,
                }}
              />
            )}
          </Box>
        ),
      },
      {
        header: 'Tracking Code',
        accessorKey: 'tracking.code',
        cell: ({ row }) => (
          <Box>
            <Link href={row.original.tracking.url} target="_blank" rel="noopener noreferrer">
              {row.original.tracking.code}
            </Link>
          </Box>
        ),
      },
      {
        header: 'Acknowledgement Form',
        meta: {
          sx: { textAlign: 'center' },
        },
        cell: ({ row }) => (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {row.original.status === 'RECEIVED' ? (
              <Chip
                size="medium"
                onClick={() => navigate(`/shipments/${row.original.id}`)}
                icon={<CheckCircleOutline color="success" />}
                label={'View Signed'}
                sx={{
                  backgroundColor: (theme) => theme.palette.success.main + '33',
                  color: (theme) => theme.palette.success.main,
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.success.main + '66',
                  },
                }}
              />
            ) : (
              <Chip
                size="medium"
                onClick={() => navigate(`/shipments/${row.original.id}`)}
                icon={<Warning color="secondary" />}
                label={'Click Here to Sign'}
                sx={{
                  backgroundColor: (theme) => theme.palette.secondary.main + '33',
                  color: (theme) => theme.palette.secondary.main,
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.secondary.main + '66',
                  },
                }}
              />
            )}
          </Box>
        ),
      },
    ];
  }, [shipments]);

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4} alignItems="center" pb={4}>
        <TableSearchInput
          value={referenceFilter}
          label="Search by Consignment Reference"
          placeholder="Consignment Reference"
          onChange={handleReferenceSearch}
        />
        <FormControlLabel
          control={<Checkbox checked={showReceived} onChange={(e) => setShowReceived(e.target.checked)} />}
          label="Show Signed"
        />
      </Stack>
      <Box ref={containerRef}>
        <Table
          data={shipments}
          columns={columns}
          isLoading={loading}
          showPagination
          manualPagination
          shouldResetPageIndex={shouldResetPageIndex}
          pageSize={pageSize}
          hasNextPage={afterCursor !== null}
          hasPrevPage={beforeCursor !== null}
          paginationLabelVariant={PaginationLabelVariant.NONE}
          paginationVariant={PaginationVariant.VARIABLE_PAGE_SIZE}
          onPaginationModelChange={handlePaginationModelChange}
          onSortingChange={handleSortingOrderChange}
          isScrollable
          maxHeight={containerHeight}
        />
      </Box>
    </Box>
  );
}

export default ConsignmentAcknowledgementsTable;
